import React, { useCallback, useMemo } from 'react'
import {
  BusSeat,
  DomesticBusCartItemOption,
  DomesticBusCartItemOptionReservation,
} from '~/__generated__/__types__'
import {
  ItemContainer,
  DescContainer,
  PriceContainer,
  WarningNotice,
  OptionNameContainer,
  OptionDescriptionContainer,
} from '~/app/components/Cart/CartItem/CartItem.styles'
import { isAvailableDomesticBusOption } from '~/app/components/Cart/CartOrderHelper'
import styled from '~/app/components/styled'
import { dateWithDayOfWeek } from '~/app/utils/datetime'
import { busGradeText, busTicketText } from '~/app/utils/filters'
import { groupBy, isNil } from 'ramda'
import { useBusStatusText, useRewardMemo } from '~/app/components/Cart/CartItem/CartItem.util'
import BusImage from '~/app/components/Cart/BusImage'
import periodEndTime from '~/app/utils/periodEndTime'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import { toKRW } from '~/app/utils/krNumberToString'
import { isNative } from '~/app/utils/typeguards'
import { getYaNative } from '@yanolja-rnd/ya-native'
import CancelPolicyText, {
  CancelPolicyFetchFailText,
} from '~/app/components/common/CancelPolicy/CancelPolicyText'
import { Icon_close, Icon_info } from '@yanolja-rnd/ya-com-system-icon'
import Checkbox from '~/app/components/common/Checkbox/YaComCheckbox'
import { Typography } from '@yanolja-rnd/ya-com-core'
import { toBusGradeOrder } from '~/api/utils/bus'
import FetchRetryButton from '~/app/components/common/FetchRetry/FetchRetryButton'

export type BusOption = Extract<
  CartItemsV3Query['cartItemsV3'][number]['options'][number],
  { __typename?: 'DomesticBusCartItemOption' }
>

interface DomesticBusItemProps {
  option: DomesticBusCartItemOption
  onCheck?: (checked: boolean, id: string) => void
  onClose?: (id: string) => void
  onTitleClick?: (id: string) => void
  onCancelRefreshButtonClick?: () => void
  pointReward?: number
  coinReward?: number
}

const TicketDescriptionWrapper = styled.div<{ disabled: boolean }>`
  color: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray600)};
  margin-top: 0.8rem;
  line-height: 1.6rem;
  .mb-4 {
    margin-bottom: 4px;
  }
`

const RewardContainer = styled.div`
  ${Typography('body2.multiline').regular};
  color: ${({ theme }) => theme.color.blue600};
`

const BusDescContainer = styled(DescContainer)`
  display: grid;
  gap: 2rem;
`

const OptionName = styled(OptionNameContainer)`
  width: calc(100% - 1.5rem);
`

const StationName = styled.span`
  ${Typography('body1').regular};
  margin-left: 0.4rem;
`

const OptionDescWrapper = styled.div`
  margin-left: 0.8rem;
`

const BusItemCheckbox = styled(Checkbox)<{ isHidden: boolean }>`
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
`

const DomesticBusItem: React.FC<DomesticBusItemProps> = ({
  option,
  onCheck,
  onClose,
  onTitleClick,
  onCancelRefreshButtonClick,
  pointReward = 0,
  coinReward = 0,
}) => {
  const isDisabled = useMemo(
    () => !isAvailableDomesticBusOption(option),
    [option.availability, option.outdated]
  )
  const byTicketType = groupBy((seat: BusSeat) => seat.ticketType)
  const rewardText = useRewardMemo(pointReward, coinReward)
  const statusText = useBusStatusText(option)
  const getTotalPriceOfReservation = useCallback(
    (reservation: DomesticBusCartItemOptionReservation) =>
      toKRW(reservation.seats.reduce((acc, cur) => acc + cur.price, 0)),
    []
  )
  const isEnabledNative = useMemo(() => {
    if (typeof window !== 'undefined') {
      const yaNative = getYaNative()
      return isNative(yaNative)
    }
    return false
  }, [])

  return (
    <>
      <ItemContainer>
        <BusDescContainer>
          {option.reservations.map((reservation, index) => (
            <div key={index}>
              <OptionName
                disabled={isDisabled}
                cursorPointerDisabled={isEnabledNative}
                onClick={() => onTitleClick?.(option.id)}
              >
                <span>{reservation.departureTime.slice(0, 5)}</span>
                <StationName>{reservation.departureTerminalName}</StationName>
                <span> ~ </span>
                <span>
                  {periodEndTime({
                    expectedArriveAt: reservation.expectedArriveAt,
                    departureDate: reservation.departureDate,
                  })}
                </span>
                <StationName>{reservation.arrivalTerminalName}</StationName>
              </OptionName>
              <div style={{ marginTop: index > 0 ? 16 : 0 }} className="row">
                <BusItemCheckbox
                  className="check-icon-wrapper"
                  onClick={() =>
                    !index && onCheck?.(isDisabled ? false : !option.isChosen, option.id)
                  }
                  checked={option.isChosen}
                  disabled={isDisabled}
                  /**
                  왕복 티켓인 경우 체크박스 아이콘은 가는편에만 노출함.
                  오는 편과 UI 위치를 맞추기 위해 아이콘만 노출하지 않고 CheckIconWrapper 만큼 영역을 차지함.
                   */
                  isHidden={!!index}
                />
                <BusImage transportationCompanyCode={reservation.transportationCompanyCode} />
                <OptionDescWrapper>
                  <OptionDescriptionContainer disabled={isDisabled}>
                    {dateWithDayOfWeek(reservation.departureDate)}
                  </OptionDescriptionContainer>
                  <TicketDescriptionWrapper disabled={isDisabled}>
                    <div className="mb-4">
                      <span>{busGradeText(reservation.busGrade)}</span>
                      <span className="bar">|</span>
                      <span>
                        {reservation.seats.map(seat => seat.seatNo).join(', ')}
                        {'번 좌석'}
                      </span>
                    </div>
                    <div>
                      {Object.entries(byTicketType(reservation.seats))
                        .filter(
                          (entry): entry is [string, BusSeat[]] =>
                            Array.isArray(entry[1]) && entry[1].length > 0
                        )
                        .sort((a, b) => (toBusGradeOrder(a[0]) > toBusGradeOrder(b[0]) ? 1 : -1))
                        .map(([key, value], index, array) => (
                          <div
                            key={key}
                            className={index !== array.length - 1 ? 'mb-4' : ''}
                          >{`${busTicketText(key)} ${toKRW(value[0].price)} x ${
                            value.length
                          }명`}</div>
                        ))}
                    </div>
                  </TicketDescriptionWrapper>
                  {option.outdated && index === option.reservations.length - 1 && (
                    <WarningNotice>
                      <Icon_info size={1.6} unit="rem" />
                      <span>상품 정보가 변경되었습니다. 상품을 다시 선택해주세요.</span>
                    </WarningNotice>
                  )}
                  {!isDisabled && !reservation.cancelPolicySummary && <CancelPolicyFetchFailText />}
                </OptionDescWrapper>
              </div>
              {!isDisabled && (
                <PriceContainer disabled={isDisabled}>
                  {!reservation.cancelPolicySummary && (
                    <FetchRetryButton onClick={onCancelRefreshButtonClick} />
                  )}
                  <div className="price-wrapper">
                    <div>
                      <span className="price">{getTotalPriceOfReservation(reservation)}</span>
                    </div>
                    <RewardContainer>
                      {rewardText && !isDisabled && <span>{rewardText}</span>}
                    </RewardContainer>
                    {reservation.cancelPolicySummary && (
                      <CancelPolicyText cancelPolicySummary={reservation.cancelPolicySummary} />
                    )}
                    {!isNil(statusText) && <div className="status-text">{statusText}</div>}
                  </div>
                </PriceContainer>
              )}
            </div>
          ))}
        </BusDescContainer>
        <div className="icon-wrapper" onClick={() => onClose?.(option.id)}>
          <Icon_close size={1.6} unit="rem" />
        </div>
      </ItemContainer>
      {/*예약 불가능한 상품에 대해서는 총 금액을 노출시킨다*/}
      {isDisabled && (
        <PriceContainer disabled={isDisabled}>
          <div className="price-wrapper">
            <div>
              <span className="price">{toKRW(option.price)}</span>
            </div>
            <RewardContainer>
              {rewardText && !isDisabled && <span>{rewardText}</span>}
            </RewardContainer>
            {!isNil(statusText) && <div className="status-text">{statusText}</div>}
          </div>
        </PriceContainer>
      )}
    </>
  )
}

export default DomesticBusItem
